import type { DealFlow, DealFlowMetric } from "../../../types/dealFunnel";
import "./DealFlowOverview.css";

function formatBn(amount: number, symbol: string): string {
  if (!Number.isFinite(amount)) return `${symbol}—`;
  const bn = amount / 1_000_000_000;
  return `${symbol}${bn.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}BN`;
}

function formatVolumesInline(m: DealFlowMetric): string {
  return [
    formatBn(m.volume_gbp, "£"),
    formatBn(m.volume_eur, "€"),
    formatBn(m.volume_usd, "$"),
  ].join("/");
}

function emptyMetric(): DealFlowMetric {
  return { count: 0, volume_gbp: 0, volume_eur: 0, volume_usd: 0 };
}

/** True inverted triangle: width tapers linearly 100% → 0% over full height. */
type FunnelStageDef = {
  key: keyof DealFlow["funnel"];
  label: string;
  color: string;
  showLess?: boolean;
  stackVolumes?: boolean;
  /** Vertical span as fraction of triangle height [start, end], 0=top 1=tip. */
  yRange: [number, number];
};

const FUNNEL_STAGES: FunnelStageDef[] = [
  {
    key: "deals_logged",
    label: "Deals Logged",
    color: "#E67E22",
    yRange: [0, 0.22],
  },
  {
    key: "deals_killed",
    label: "Deals Killed",
    color: "#C0392B",
    showLess: true,
    yRange: [0.22, 0.44],
  },
  {
    key: "lost_to_other_lenders",
    label: "Lost to Other Lenders",
    color: "#8E9A9D",
    showLess: true,
    yRange: [0.44, 0.66],
  },
  {
    key: "active_deals",
    label: "Active Deals",
    color: "#1E7A4A",
    stackVolumes: true,
    yRange: [0.66, 1],
  },
];

type BreakdownDef = {
  key: keyof DealFlow["active_breakdown"];
  label: string;
  bg: string;
};

const BREAKDOWN_ITEMS: BreakdownDef[] = [
  { key: "closed", label: "Closed", bg: "#1B5E3B" },
  { key: "closed_apollo", label: "Closed - Apollo", bg: "#2C7A5A" },
  { key: "signed_term_sheets", label: "Signed Term Sheets", bg: "#1A3352" },
  { key: "pending_responses", label: "Pending Responses", bg: "#7FB3C9" },
  { key: "in_screening", label: "In Screening", bg: "#9A8B7A" },
  { key: "apollo_referrals", label: "Apollo Referrals", bg: "#A8AEB5" },
];

const SVG_W = 720;
const SVG_H = 620;
const CX = SVG_W / 2;

/** Half-width as fraction of full width at vertical fraction t (0 top → 1 tip). */
function halfWidthAt(t: number): number {
  return 0.5 * (1 - t);
}

function bandPolygon(y0: number, y1: number): string {
  const topL = (0.5 - halfWidthAt(y0)) * SVG_W;
  const topR = (0.5 + halfWidthAt(y0)) * SVG_W;
  const botL = (0.5 - halfWidthAt(y1)) * SVG_W;
  const botR = (0.5 + halfWidthAt(y1)) * SVG_W;
  const topY = y0 * SVG_H;
  const botY = y1 * SVG_H;
  return `${topL},${topY} ${topR},${topY} ${botR},${botY} ${botL},${botY}`;
}

export function DealFlowOverview(props: { dealFlow: DealFlow }) {
  const { dealFlow } = props;
  const logged = dealFlow.funnel.deals_logged ?? emptyMetric();
  const active = dealFlow.funnel.active_deals ?? emptyMetric();

  return (
    <article className="deal-flow-slide">
      <header className="deal-flow-slide__titlebar">
        <h2 className="deal-flow-slide__title">
          Pipeline Analysis – Deal Flow Summary
        </h2>
      </header>

      <p className="deal-flow-slide__summary">
        {logged.count.toLocaleString("en-GB")} opportunities have been reviewed
        to date, with {active.count.toLocaleString("en-GB")} active deals
        currently progressing through closing, referral and screening stages.
      </p>

      <div className="deal-flow-slide__body">
        <div className="deal-flow-slide__funnel">
          {/* Native SVG text (no foreignObject) so PDF capture matches the UI */}
          <svg
            className="deal-flow-slide__funnel-svg"
            viewBox={`0 0 ${SVG_W} ${SVG_H}`}
            width="100%"
            role="img"
            aria-label="Deal flow funnel"
          >
            {FUNNEL_STAGES.map((stage) => {
              const [y0, y1] = stage.yRange;
              const metric = dealFlow.funnel[stage.key] ?? emptyMetric();
              const bandH = (y1 - y0) * SVG_H;
              // Keep "Less" on the seam; place the text block clear of it
              const lessY = y0 * SVG_H + 11;
              const contentTop = stage.showLess
                ? y0 * SVG_H + 26
                : y0 * SVG_H + 8;
              const contentBottom = y1 * SVG_H - 8;
              const midY = (contentTop + contentBottom) / 2;

              return (
                <g key={stage.key}>
                  <polygon
                    points={bandPolygon(y0, y1)}
                    fill={stage.color}
                  />
                  {stage.showLess ? (
                    <text
                      x={CX}
                      y={lessY}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="deal-flow-slide__svg-less"
                    >
                      Less
                    </text>
                  ) : null}

                  {stage.stackVolumes ? (
                    <>
                      <text
                        x={CX}
                        y={midY - bandH * 0.18}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="deal-flow-slide__svg-count"
                      >
                        {metric.count.toLocaleString("en-GB")}
                      </text>
                      <text
                        x={CX}
                        y={midY + 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="deal-flow-slide__svg-label"
                      >
                        {stage.label}
                      </text>
                      <text
                        x={CX}
                        y={midY + 22}
                        textAnchor="middle"
                        className="deal-flow-slide__svg-vols"
                      >
                        <tspan x={CX} dy="0">
                          {formatBn(metric.volume_gbp, "£")}
                        </tspan>
                        <tspan x={CX} dy="15">
                          {formatBn(metric.volume_eur, "€")}
                        </tspan>
                        <tspan x={CX} dy="15">
                          {formatBn(metric.volume_usd, "$")}
                        </tspan>
                      </text>
                    </>
                  ) : (
                    <>
                      <text
                        x={CX}
                        y={midY - 20}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="deal-flow-slide__svg-count"
                      >
                        {metric.count.toLocaleString("en-GB")}
                      </text>
                      <text
                        x={CX}
                        y={midY + 8}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="deal-flow-slide__svg-label"
                      >
                        {stage.label}
                      </text>
                      <text
                        x={CX}
                        y={midY + 28}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="deal-flow-slide__svg-vols"
                      >
                        {formatVolumesInline(metric)}
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        <div className="deal-flow-slide__breakdown">
          <div className="deal-flow-slide__hub">
            {active.count.toLocaleString("en-GB")} Active Deals
          </div>
          <div className="deal-flow-slide__stem" aria-hidden />
          <ul className="deal-flow-slide__branches">
            {BREAKDOWN_ITEMS.map((item) => {
              const metric =
                dealFlow.active_breakdown[item.key] ?? emptyMetric();
              return (
                <li key={item.key} className="deal-flow-slide__branch">
                  <div
                    className="deal-flow-slide__status"
                    style={{ backgroundColor: item.bg }}
                  >
                    {metric.count.toLocaleString("en-GB")} {item.label}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </article>
  );
}
